import {
  BPROJ_FORMAT_VERSION,
  formatRevisionEtag,
  type CurrentUser,
  type ProjectListItem,
} from '@bigint-project/cloud-contracts';
import {
  deserializeProjectTemplate,
  instantiateProjectTemplate,
  PROJECT_TEMPLATE_KIND,
  type ProjectTemplate,
} from '@desktop/persistence/project-template';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactElement } from 'react';

import type { CloudApi } from '../api/client';
import { errorMessage } from '../api/client';
import { EmptyMessage, ErrorMessage, Loading } from './Status';
import { LogoutButton } from './LogoutButton';
import { DesktopDownload } from './DesktopDownload';
import { TemplateCreateDialog, type TemplateCreateInput } from './TemplateCreateDialog';

interface ProjectDashboardProps {
  readonly api: CloudApi;
  readonly user: CurrentUser;
  readonly onOpenProject: (id: string) => void;
  readonly onOpenLocal: () => void;
  readonly onLoggedOut: () => void;
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-TW');
}

/** 副檔名被改掉時仍要走範本流程，否則範本信封會被當成 `.bproj` 送進匯入 API 而失敗。 */
function isTemplateEnvelope(value: unknown): boolean {
  return typeof value === 'object' && value !== null &&
    (value as { readonly kind?: unknown }).kind === PROJECT_TEMPLATE_KIND;
}

/** 解析失敗一律中止匯入；錯誤語彙對齊桌面版的「讀取範本失敗」。 */
function readTemplate(text: string): ProjectTemplate {
  try {
    return deserializeProjectTemplate(text);
  } catch (reason) {
    throw new Error(`讀取範本失敗：${reason instanceof Error ? reason.message : String(reason)}`);
  }
}

export function ProjectDashboard({ api, user, onOpenProject, onOpenLocal, onLoggedOut }: ProjectDashboardProps): ReactElement {
  const [projects, setProjects] = useState<readonly ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<ProjectTemplate | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const importButtonRef = useRef<HTMLButtonElement>(null);

  const reload = async (): Promise<void> => {
    setError(null);
    try {
      setProjects(await api.listProjects());
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void api.listProjects().then(
      (result) => {
        if (!cancelled) setProjects(result);
      },
      (reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const createProject = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const nextName = name.trim();
    if (nextName.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const project = await api.createProject(nextName);
      onOpenProject(project.id);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSubmitting(false);
    }
  };

  const importProject = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    setSubmitting(true);
    setError(null);
    try {
      const text = await file.text();
      if (file.name.toLowerCase().endsWith('.bprojt')) {
        setPendingTemplate(readTemplate(text));
        return;
      }
      const raw = JSON.parse(text) as unknown;
      if (isTemplateEnvelope(raw)) {
        setPendingTemplate(readTemplate(text));
        return;
      }
      const project = await api.importProject(raw);
      onOpenProject(project.id);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSubmitting(false);
    }
  };

  /** 範本先在瀏覽器端實體化成完整 Project，再包成 `.bproj` 信封走既有匯入 API 建立雲端專案。 */
  const createFromTemplate = async (input: TemplateCreateInput): Promise<void> => {
    if (pendingTemplate === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const project = instantiateProjectTemplate(pendingTemplate, input);
      const created = await api.importProject({
        formatVersion: BPROJ_FORMAT_VERSION,
        savedAt: new Date().toISOString(),
        project,
        view: null,
      });
      setPendingTemplate(null);
      onOpenProject(created.id);
    } catch (reason) {
      setError(`從範本建立專案失敗：${errorMessage(reason)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const setArchived = async (project: ProjectListItem, archived: boolean): Promise<void> => {
    setError(null);
    try {
      await api.updateProjectMetadata(project.id, { archived }, formatRevisionEtag(project.serverRevision));
      await reload();
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };

  const visibleProjects = projects.filter((project) => showArchived || project.archivedAt === null);

  return (
    <main className="page-shell">
      <header className="page-header">
        <div>
          <p className="eyebrow">BigInt Project Cloud</p>
          <h1>專案</h1>
          <p className="muted">{user.displayName} · {user.email}</p>
        </div>
        <div className="card-actions">
          <button type="button" className="secondary" onClick={onOpenLocal}>本機模式</button>
          <button type="button" className="secondary" onClick={() => void reload()}>重新整理</button>
          <LogoutButton api={api} onLoggedOut={onLoggedOut} />
        </div>
      </header>

      <DesktopDownload />

      <section className="dashboard-actions" aria-label="建立或匯入專案">
        <form onSubmit={(event) => void createProject(event)}>
          <label htmlFor="project-name">新專案名稱</label>
          <div className="inline-form">
            <input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={160}
              disabled={submitting}
            />
            <button type="submit" disabled={submitting || name.trim().length === 0}>建立</button>
          </div>
        </form>
        <div>
          <span className="field-label">從桌面版匯入</span>
          <button ref={importButtonRef} type="button" className="secondary" disabled={submitting} onClick={() => importRef.current?.click()}>
            匯入 .bproj / .bprojt
          </button>
          <input
            ref={importRef}
            className="visually-hidden"
            type="file"
            accept=".bproj,.bprojt,application/json"
            onChange={(event) => void importProject(event)}
          />
        </div>
      </section>

      {pendingTemplate !== null && (
        <TemplateCreateDialog
          template={pendingTemplate}
          submitting={submitting}
          returnFocusRef={importButtonRef}
          onCancel={() => setPendingTemplate(null)}
          onCreate={(input) => void createFromTemplate(input)}
        />
      )}

      <label className="checkbox-row">
        <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />
        顯示已封存專案
      </label>

      {error !== null && <ErrorMessage message={error} />}
      {loading && <Loading />}
      {!loading && visibleProjects.length === 0 && <EmptyMessage>目前沒有專案。</EmptyMessage>}
      <section className="project-grid" aria-label="專案清單">
        {visibleProjects.map((project) => (
          <article key={project.id} className="project-card">
            <div>
              <span className={`role-badge role-${project.role}`}>{project.role}</span>
              {project.archivedAt !== null && <span className="archived-badge">已封存</span>}
              <h2>{project.name}</h2>
              <p className="muted">更新於 {formatUpdatedAt(project.updatedAt)} · rev {project.serverRevision}</p>
            </div>
            <div className="card-actions">
              <button type="button" onClick={() => onOpenProject(project.id)}>開啟</button>
              {project.role === 'owner' && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => void setArchived(project, project.archivedAt === null)}
                >
                  {project.archivedAt === null ? '封存' : '取消封存'}
                </button>
              )}
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
